import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material'
import Paper from '@mui/material/Paper'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { fetchBusDriverSignOnsAction } from '../actions/bus-driver-sign-ons'
import Page from '../components/page/Page'
import { useTypedDispatch, useTypedSelector } from '../index'
import moment from '../lib/moment-fi'
import {
  selectBusDriverSignOns,
  selectBusDriverSignOnsError,
  selectBusDriverSignOnsStatus,
} from '../reducers/busDriverSignOnsSlice'
import { BusDriverSignOn } from '../types/Input'

type TableColumnsType = 'tagId' | 'employeeId' | 'checkInTime'

type Order = 'asc' | 'desc'

const descendingComparator = (
  a: BusDriverSignOn,
  b: BusDriverSignOn,
  orderBy: TableColumnsType
) => {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }

  if (b[orderBy] > a[orderBy]) {
    return 1
  }

  return 0
}

const getComparator = (order: Order, orderBy: TableColumnsType) => {
  return order === 'desc'
    ? (a: BusDriverSignOn, b: BusDriverSignOn) => descendingComparator(a, b, orderBy)
    : (a: BusDriverSignOn, b: BusDriverSignOn) => -descendingComparator(a, b, orderBy)
}

const formatCheckInTime = (checkInTime: string) => moment(checkInTime).format('L LT')

const BusDriverCheckInsListPage = () => {
  const dispatch = useTypedDispatch()
  const signOns = useTypedSelector(selectBusDriverSignOns)
  const status = useTypedSelector(selectBusDriverSignOnsStatus)
  const error = useTypedSelector(selectBusDriverSignOnsError)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [orderBy, setOrderBy] = useState<TableColumnsType>('checkInTime')
  const [order, setOrder] = useState<Order>('asc')
  const { t } = useTranslation()

  useEffect(() => {
    dispatch(fetchBusDriverSignOnsAction())
  }, [dispatch])

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleRequestSort = (property: TableColumnsType) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    setPage(0)
  }

  const visibleRows = useMemo(
    () =>
      [...signOns]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, signOns]
  )

  return (
    <Page menu="options">
      <Box
        sx={{
          padding: 2,
          marginY: 20,
          maxWidth: '800px',
          overflow: 'hidden',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          {t('busDriverSignIn.table.title')}
        </Typography>
        {status === 'loading' && <Typography>{t('loading')}</Typography>}
        {status === 'failed' && <Typography color="error">{error}</Typography>}
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="Bussinkuljettajien työvuorotiedot" size="small">
            <TableHead>
              <TableRow>
                <TableCell key="tagId" sortDirection={orderBy === 'tagId' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'tagId'}
                    direction={orderBy === 'tagId' ? order : 'asc'}
                    onClick={() => handleRequestSort('tagId')}
                  >
                    {t('busDriverSignIn.table.tagId')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  key="employeeId"
                  sortDirection={orderBy === 'employeeId' ? order : false}
                  align="right"
                >
                  <TableSortLabel
                    active={orderBy === 'employeeId'}
                    direction={orderBy === 'employeeId' ? order : 'asc'}
                    onClick={() => handleRequestSort('employeeId')}
                  >
                    {t('userNumber')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  key="checkInTime"
                  sortDirection={orderBy === 'checkInTime' ? order : false}
                  align="right"
                >
                  <TableSortLabel
                    active={orderBy === 'checkInTime'}
                    direction={orderBy === 'checkInTime' ? order : 'asc'}
                    onClick={() => handleRequestSort('checkInTime')}
                  >
                    {t('busDriverSignIn.table.checkInTime')}
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row, index) => (
                <TableRow
                  key={`${row.id}-${page}-${index}`}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {row.tagId}
                  </TableCell>
                  <TableCell align="right">{row.employeeId}</TableCell>
                  <TableCell align="right">{formatCheckInTime(row.checkInTime)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[20, 50, 100]}
          component="div"
          count={signOns.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rivejä per sivu"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
        />
      </Box>
    </Page>
  )
}

export default BusDriverCheckInsListPage
